import {
  useState,
  useEffect,
  useCallback,
  useRef,
  startTransition,
  Dispatch,
  SetStateAction,
} from 'react';

// Types
import { Project, ProjectMember } from '@shared/types';

// Actions
import { getProjectMember } from '@/actions/client';

// Constants
import { ERROR_MESSAGES } from '@/constants/messages';

// Utils
import { showErrorToast } from '@/utils/errorHandlers';
import { formatProjectMembers, sortProjectMembers } from '@/utils';

// Providers
import { useFirebase } from '@/providers/firebase';

interface ProjectMembersState {
  members: ProjectMember[];
  isLoading: boolean;
  error: string | null;
  isInitialLoaded: boolean;
  refetch: () => Promise<void>;
}

type ProjectMembersParamsType = {
  project: Project | null;
  members: ProjectMember[];
  isInitialized: boolean;
  setMembers: Dispatch<SetStateAction<ProjectMember[]>>;
};

export const useGetProjectMembers = ({
  project,
  isInitialized,
  members,
  setMembers,
}: ProjectMembersParamsType): ProjectMembersState => {
  const projectId = project?.projectInfo?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const { getToken } = useFirebase();

  const hasInitialized = useRef(false);

  const fetchMembers = useCallback(async () => {
    if (!projectId || isLoading) return;

    const token = await getToken();

    setIsLoading(true);
    setError(null);

    try {
      const response = await getProjectMember({
        projectId,
        requestConfig: { token },
      });

      if (response.error || !response.data) {
        const errorMessage =
          response.error || ERROR_MESSAGES.FETCH_PROJECT_MEMBERS;

        setError(errorMessage);
        showErrorToast(errorMessage);
        setIsLoading(false);
        setIsInitialLoaded(true);
        return;
      }

      const formattedData = formatProjectMembers(response.data);
      const sortedData = sortProjectMembers(formattedData);

      startTransition(() => {
        setMembers(sortedData);
        setIsLoading(false);
        setIsInitialLoaded(true);
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.FETCH_PROJECT_MEMBERS;

      setError(errorMessage);
      showErrorToast(errorMessage);
      setIsLoading(false);
      setIsInitialLoaded(true);
    }

    // Don't need to put "getToken" in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isLoading, setMembers]);

  const refetch = useCallback(async () => {
    await fetchMembers();
  }, [fetchMembers]);

  // Reset state when project changes
  useEffect(() => {
    hasInitialized.current = false;
    setMembers([]);
    setError(null);
    setIsInitialLoaded(false);
    setIsLoading(false);
  }, [projectId, setMembers]);

  // Initialize members fetch when project is ready
  useEffect(() => {
    const shouldInitialize =
      isInitialized && projectId && !hasInitialized.current;

    if (shouldInitialize) {
      hasInitialized.current = true;
      fetchMembers();
    }
  }, [isInitialized, projectId, fetchMembers]);

  return {
    members,
    isLoading,
    error,
    isInitialLoaded,
    refetch,
  };
};
